import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, LoaderCircle, RotateCcw, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/_shared/components/ui/button';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useAuth } from '@/_shared/hooks/useAuth';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { cn } from '@/_shared/lib/utils';

import { DashboardChatSidebar } from '../components/DashboardChatSidebar';
import { DashboardHeader } from '../components/DashboardHeader';
import { DashboardPanel } from '../components/DashboardPanel';
import { DashboardParamBar } from '../components/DashboardParamBar';
import { useDashboardChat } from '../hooks/useDashboardChat';
import { useDynamicParamOptions } from '../hooks/useDynamicParamOptions';
import { computeDefaults, searchParamsToValues, valuesToSearchParams } from '../lib/dashboard-params';
import type { DashboardPanel as DashboardPanelType } from '../types';

export function Dashboard(): React.ReactElement {
  const { dashSlug: slug } = useParams<{ dashSlug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { wp, isAdmin } = useWorkspace();
  const { user } = useAuth();
  const orpc = useOrpc();

  // Fetch dashboard
  const {
    data: dashboardData,
    isLoading,
    error,
  } = useQuery(
    orpc.dashboard.get.queryOptions({
      input: slug ? { slug } : { slug: '' },
    }),
  );
  const dashboard = dashboardData?.dashboard ?? null;
  usePageTitle(dashboard?.title);

  // Resolve dynamic parameter options (params with sql field)
  const {
    parameters: resolvedParams,
    isLoading: paramsLoading,
    loadingParamNames,
  } = useDynamicParamOptions(dashboard?.id, dashboard?.spec.parameters ?? [], orpc);

  // Merge defaults with URL search params
  const paramValues = useMemo((): Record<string, string | string[]> => {
    if (!dashboard) return {};
    const defaults = computeDefaults(resolvedParams);
    const fromUrl = searchParamsToValues(searchParams, resolvedParams);
    return { ...defaults, ...fromUrl };
  }, [dashboard, resolvedParams, searchParams]);

  // Deferred defaults: seed missing params AND auto-correct invalid option values once loaded
  useEffect(() => {
    if (paramsLoading || !dashboard) return;
    const patch: Record<string, string | string[]> = {};
    for (const p of resolvedParams) {
      if (p.type === 'select' && p.sql && p.options?.length) {
        const currentVal = searchParams.get(p.name);
        const hasValidValue = currentVal !== null && p.options.some((o) => o.value === currentVal);
        if (!hasValidValue) {
          patch[p.name] = p.options[0]!.value;
        }
      } else if (p.type === 'multi_select' && p.sql && p.options?.length) {
        const currentVals = searchParams.getAll(p.name);
        const validVals = currentVals.filter((v) => p.options!.some((o) => o.value === v));
        if (validVals.length === 0) {
          patch[p.name] = p.options.map((o) => o.value);
        }
      }
    }
    if (Object.keys(patch).length > 0) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (Array.isArray(v)) {
              for (const val of v) next.append(k, val);
            } else {
              next.set(k, v);
            }
          }
          return next;
        },
        { replace: true },
      );
    }
  }, [paramsLoading, resolvedParams, dashboard, searchParams, setSearchParams]);

  // Sync param changes to URL
  const handleParamChange = useCallback(
    (newValues: Record<string, string | string[]>) => {
      setSearchParams(valuesToSearchParams(newValues), { replace: true });
    },
    [setSearchParams],
  );

  // Plaintext share token, fresh from a mint (publish or regenerate). The
  // backend never returns it on dashboard.get — it lives only in component
  // state for the popover, and disappears as soon as the user navigates away.
  const [plaintextToken, setPlaintextToken] = useState<string | null>(null);

  const invalidateDashboardQueries = useCallback((): void => {
    if (slug) {
      void queryClient.invalidateQueries({
        queryKey: orpc.dashboard.get.queryOptions({ input: { slug } }).queryKey,
      });
    }
    void queryClient.invalidateQueries({
      queryKey: orpc.dashboard.list.queryOptions({ input: {} }).queryKey,
    });
  }, [orpc, queryClient, slug]);

  const privacyMutation = useMutation(
    orpc.dashboard.updatePrivacy.mutationOptions({
      onSuccess: (result) => {
        // updatePrivacy returns { ok: true, token } when going public,
        // { ok: true } when going private. Capture the fresh token; clear
        // it on unpublish so the stale value doesn't bleed across renders.
        const token = 'token' in result ? result.token : null;
        setPlaintextToken(token ?? null);
        invalidateDashboardQueries();
        toast.success(token ? 'Published — copy the share link to share' : 'Unpublished — link is now inactive');
      },
      onError: (err: { message?: string }) => {
        // A silent failure here is a privacy regression: e.g. a 429 on
        // unpublish would leave the user thinking the dashboard is now
        // private when it is still public.
        toast.error(err.message ?? 'Failed to update visibility');
      },
    }),
  );

  const regenerateMutation = useMutation(
    orpc.dashboard.regenerateShareToken.mutationOptions({
      onSuccess: (result) => {
        setPlaintextToken(result.token);
        invalidateDashboardQueries();
        toast.success('New share link minted');
      },
      onError: (err: { message?: string }) => {
        // Critical: silent failure here means the user shares a "rotated"
        // link that is actually the still-leaked old token.
        toast.error(err.message ?? 'Failed to rotate share link');
      },
    }),
  );

  const handlePublish = useCallback((): void => {
    if (!dashboard) return;
    privacyMutation.mutate({ dashboardId: dashboard.id, isPublic: true });
  }, [dashboard, privacyMutation]);

  const handleUnpublish = useCallback((): void => {
    if (!dashboard) return;
    privacyMutation.mutate({ dashboardId: dashboard.id, isPublic: false });
  }, [dashboard, privacyMutation]);

  const handleRegenerate = useCallback((): void => {
    if (!dashboard) return;
    // Confirm before rotation: the existing link dies the moment we mint a
    // new one, and the backend has no way to undo it.
    const ok = window.confirm('Anyone holding the current link will lose access. Continue?');
    if (!ok) return;
    regenerateMutation.mutate({ dashboardId: dashboard.id });
  }, [dashboard, regenerateMutation]);

  const deleteMutation = useMutation(
    orpc.dashboard.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.dashboard.list.queryOptions({ input: {} }).queryKey,
        });
        toast.success('Dashboard deleted');
        void navigate(wp('/dashboards'));
      },
      onError: (err: { message?: string }) => {
        toast.error(err.message ?? 'Failed to delete dashboard');
      },
    }),
  );

  const handleDelete = useCallback(() => {
    if (!dashboard) return;
    deleteMutation.mutate({ id: dashboard.id });
  }, [dashboard, deleteMutation]);

  // Sort panels by order

  const sortedPanels = useMemo(() => {
    if (!dashboard) return [];
    return [...dashboard.spec.panels].sort((a, b) => a.order - b.order);
  }, [dashboard]);

  // Dashboard chat sidebar
  const chat = useDashboardChat(dashboard, paramValues);
  const { openAndSend, toggle: toggleChat, close: closeChat, isOpen: chatIsOpen } = chat;

  // Panel → sidebar assistant actions

  const handleAskAssistant = useCallback(
    (panelTitle: string, error: string | null) => {
      if (error) {
        openAndSend(`The panel "${panelTitle}" has an error:\n\n${error}\n\nPlease investigate and fix it.`);
      } else {
        openAndSend(`Explain the data in the panel "${panelTitle}" — what patterns or insights do you see?`);
      }
    },
    [openAndSend],
  );

  // Keyboard shortcuts: Cmd+Shift+K to toggle sidebar, Escape to close
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        toggleChat();
        return;
      }
      if (e.key === 'Escape' && chatIsOpen) {
        e.preventDefault();
        closeChat();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleChat, closeChat, chatIsOpen]);

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      {isLoading && (
        <div className="flex flex-1 items-center justify-center">
          <LoaderCircle size={24} className="animate-spin text-muted-foreground" />
        </div>
      )}

      {error && (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground">
          <CircleAlert size={32} />
          <p className="text-sm">Dashboard not found</p>
        </div>
      )}

      {dashboard && (
        <div className="flex min-h-0 flex-1 overflow-hidden">
          {/* Left: dashboard content — own scroll */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <DashboardHeader
              dashboard={dashboard}
              onToggleChat={chat.toggle}
              chatOpen={chat.isOpen}
              isAdmin={isAdmin || dashboard.createdBy === user?.id}
              onDelete={handleDelete}
              deletePending={deleteMutation.isPending}
              plaintextToken={plaintextToken}
              onPublish={handlePublish}
              onUnpublish={handleUnpublish}
              onRegenerate={handleRegenerate}
              regeneratePending={regenerateMutation.isPending}
              privacyPending={privacyMutation.isPending}
            />
            <DashboardParamBar
              parameters={resolvedParams}
              values={paramValues}
              onChange={handleParamChange}
              loadingParamNames={loadingParamNames}
            />
            <div className="flex-1 overflow-y-auto">
              <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 px-6 py-6 lg:grid-cols-2">
                {sortedPanels.map((panel) => (
                  <PanelWithData
                    key={panel.id}
                    dashboardId={dashboard.id}
                    panel={panel}
                    params={paramValues}
                    paramsLoading={paramsLoading}
                    onAskAssistant={handleAskAssistant}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right: chat sidebar — own scroll, input pinned at bottom */}
          <div
            id="dashboard-chat-sidebar"
            className={cn(
              'flex min-h-0 shrink-0 flex-col overflow-hidden border-l border-border/40 bg-background transition-[width] duration-300',
              chat.isOpen ? 'w-[23.75rem]' : 'w-0 border-l-0',
            )}
          >
            <div className="flex min-h-0 w-[23.75rem] flex-1 flex-col overflow-hidden">
              {/* Sidebar header — outside scrollable content */}
              <div className="flex items-center justify-between border-b border-border/40 px-3 py-2.5">
                <span className="text-sm font-semibold text-foreground">Dashboard Assistant</span>
                <div className="flex items-center gap-1">
                  {chat.messages.length > 0 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={chat.resetChat}
                      aria-label="Reset conversation"
                    >
                      <RotateCcw size={12} />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={chat.close}
                    aria-label="Close sidebar"
                  >
                    <X size={12} />
                  </Button>
                </div>
              </div>
              <DashboardChatSidebar
                dashboard={dashboard}
                paramValues={paramValues}
                messages={chat.messages}
                agentActive={chat.agentActive}
                canStop={chat.canStop}
                disabled={!chat.sessionReady}
                onSend={chat.sendMessage}
                onReset={chat.resetChat}
                onClose={chat.close}
                onStop={chat.stopTurn}
                prefillValue={chat.prefillValue}
                prefillNonce={chat.prefillNonce}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Panel with data fetching ───

function PanelWithData({
  dashboardId,
  panel,
  params,
  paramsLoading,
  onAskAssistant,
}: {
  dashboardId: string;
  panel: DashboardPanelType;
  params: Record<string, string | string[]>;
  paramsLoading?: boolean;
  onAskAssistant?: (panelTitle: string, error: string | null) => void;
}): React.ReactElement {
  const orpc = useOrpc();
  const queryOpts = orpc.dashboard.query.queryOptions({
    input: {
      dashboardId,
      panelId: panel.id,
      params,
    },
  });
  const { data, isLoading, error } = useQuery({
    ...queryOpts,
    enabled: !paramsLoading,
  });

  const errorMsg = error ? String(error.message ?? 'Query failed') : null;

  return (
    <div className={panel.width === 'full' ? 'lg:col-span-2' : 'lg:col-span-1'}>
      <DashboardPanel
        panel={panel}
        data={data ?? null}
        loading={isLoading}
        error={errorMsg}
        onAskAssistant={onAskAssistant ? () => onAskAssistant(panel.title, errorMsg) : undefined}
      />
    </div>
  );
}
